import {
  buildNotificationChannelContent,
  sanitizeInAppNotificationContent,
  NotificationTemplateKey,
} from "@config/notification-templates.config";
import { userNotificationRepo } from "@repositories";
import {
  buildNotificationNavigateActions,
  buildNotificationPushMainAction,
} from "@shared/notifications/notification-navigation";
import { userRepo } from "@repositories";
import {
  sendMessageToRecipients,
  sendPushToUsers,
} from "./notifications-facade";

export type TemplatedNotificationChannels = {
  push?: boolean;
  sms?: boolean;
  inApp?: boolean;
};

export type SendTemplatedUserNotificationParams = {
  templateKey: NotificationTemplateKey;
  placeholders: Record<string, string>;
  channels: TemplatedNotificationChannels;
  userId?: string;
  phoneNumber?: string;
  /** In-app button + push tap destination */
  navigateTo?: string;
};

/**
 * Sends a templated notification via selected channels in parallel (not fallback).
 * Push uses short title/body; SMS uses the full template; in-app uses short text without URLs.
 * For NestJS server code prefer NotificationsService.sendTemplatedUserNotification.
 */
export async function sendTemplatedUserNotification(
  params: SendTemplatedUserNotificationParams,
): Promise<void> {
  const content = buildNotificationChannelContent(
    params.templateKey,
    params.placeholders,
  );
  const { userId, channels, navigateTo } = params;
  let phoneNumber = params.phoneNumber;

  if (userId) {
    const user = await userRepo.findId(userId);
    if (user?.phoneNumber && phoneNumber == null) {
      phoneNumber = user.phoneNumber;
    }
  }

  const shouldSendSms =
    !!phoneNumber &&
    (channels.sms || (userId != null && params.phoneNumber == null));

  const actions = buildNotificationNavigateActions(navigateTo);
  const mainAction = buildNotificationPushMainAction(navigateTo);

  const tasks: Promise<unknown>[] = [];

  if (channels.inApp && userId) {
    tasks.push(
      userNotificationRepo.insert({
        toId: userId,
        title: content.inAppTitle,
        content: sanitizeInAppNotificationContent(content.inAppContent),
        icon: content.icon,
        actions,
      }),
    );
  }

  if (channels.push && userId) {
    tasks.push(
      sendPushToUsers(
        {
          title: content.pushTitle,
          body: content.pushBody,
          mainAction,
        },
        [userId],
      ),
    );
  }

  if (shouldSendSms) {
    tasks.push(
      sendMessageToRecipients(
        { message: content.smsMessage },
        [{ phoneNumber: phoneNumber!, email: "" }],
        ["sms"],
      ),
    );
  }

  const results = await Promise.allSettled(tasks);
  for (const result of results) {
    if (result.status === "rejected") {
      console.error(
        "[sendTemplatedUserNotification] channel failed:",
        params.templateKey,
        result.reason,
      );
    }
  }
}

const DEFERRED_PREFIX = "__deferred_v1:";
const DEFERRED_RETRY_PREFIX = "__deferred_retry_v1:";

export type DeferredTemplatedNotification = {
  templateKey: NotificationTemplateKey;
  placeholders: Record<string, string>;
  channels: TemplatedNotificationChannels;
  navigateTo?: string;
  phoneNumber?: string;
};

export type DeferredRetryTemplatedNotification = {
  /** ISO datetime - do not attempt before this time */
  notBefore: string;
  attempt: number;
  maxAttempts: number;
  /** Used for de-duplication / observability */
  idempotencyKey?: string;
  payload: DeferredTemplatedNotification;
};

export function encodeDeferredTemplatedNotification(
  payload: DeferredTemplatedNotification,
): string {
  return DEFERRED_PREFIX + JSON.stringify(payload);
}

export function encodeDeferredRetryTemplatedNotification(
  payload: DeferredRetryTemplatedNotification,
): string {
  return DEFERRED_RETRY_PREFIX + JSON.stringify(payload);
}

export function decodeDeferredTemplatedNotification(
  content: string,
): DeferredTemplatedNotification | null {
  if (!content.startsWith(DEFERRED_PREFIX)) return null;
  try {
    return JSON.parse(
      content.slice(DEFERRED_PREFIX.length),
    ) as DeferredTemplatedNotification;
  } catch {
    return null;
  }
}

export function decodeDeferredRetryTemplatedNotification(
  content: string,
): DeferredRetryTemplatedNotification | null {
  if (!content.startsWith(DEFERRED_RETRY_PREFIX)) return null;
  try {
    return JSON.parse(
      content.slice(DEFERRED_RETRY_PREFIX.length),
    ) as DeferredRetryTemplatedNotification;
  } catch {
    return null;
  }
}

export function isDeferredTemplatedNotification(content: string): boolean {
  return content.startsWith(DEFERRED_PREFIX);
}

export function isDeferredRetryTemplatedNotification(content: string): boolean {
  return content.startsWith(DEFERRED_RETRY_PREFIX);
}
